import React, { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Table, Spinner, Badge } from 'reactstrap';
import { BarChart2, DollarSign, TrendingUp, TrendingDown, Minus } from 'react-feather';
import axios from 'axios';
import API_BASE_URL from '../../../../Config/api';
import ConfigDB from '../../../../Config/ThemeConfig';
import authHeader from '../../../Customer/authHeader';
import AnimatedNumber, { useCountUp } from '../../../../_helper/AnimatedNumber';

// Shared "Monthly Breakdown" widgets — Orders-by-Collection stacked chart
// (this year vs last year) plus a Monthly Revenue table. Fully self-
// contained: fetches its own data and always compares the current calendar
// year against the previous one (computed from Date, never hardcoded), so
// it can be dropped onto any page — the Order Analytics page and the
// dashboard both render this exact same component to stay in sync.

const PRIMARY = localStorage.getItem('default_color') || ConfigDB.data.color.primary_color;
const MUTED = '#b7bcc5';

// Distinct colors for the top stacked-bar segments; the last one is reserved
// for the "Other" bucket that overflow buckets roll into.
const MONTHLY_PALETTE = ['#4C6EF5', '#F76707', '#2F9E44', '#E64980', '#7048E8', '#ADB5BD'];

// Blends a hex color toward white so the "same month last year" segments
// read as a paler version of each bucket's own color, instead of a flat
// block — keeps the two bars comparable at a glance without a second legend.
const lightenHex = (hex, amount = 0.6) => {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  const r = mix((num >> 16) & 255);
  const g = mix((num >> 8) & 255);
  const b = mix(num & 255);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const formatNumber = (v) => new Intl.NumberFormat('en-IE').format(v || 0);

const percentDelta = (a, b) => {
  if (!a) return b ? 100 : 0;
  return Math.round(((b - a) / a) * 1000) / 10;
};

const DeltaBadge = ({ value }) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <Badge color="light" className="text-dark">—</Badge>;
  }
  if (value === 0) {
    return (
      <Badge color="secondary" className="d-inline-flex align-items-center gap-1">
        <Minus size={12} /> 0%
      </Badge>
    );
  }
  const up = value > 0;
  return (
    <Badge color={up ? 'success' : 'danger'} className="d-inline-flex align-items-center gap-1">
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {up ? '+' : ''}
      {value}%
    </Badge>
  );
};

// Renders an SVG <text> whose content counts up to `value` — used for the
// bold total printed above each bar, so it lands on the real number instead
// of just appearing when the chart loads.
const AnimatedSvgText = ({ value, ...textProps }) => {
  const animated = useCountUp(value);
  return <text {...textProps}>{formatNumber(Math.round(animated))}</text>;
};

// Hand-built SVG bar chart. ApexCharts' grouped-stacked bars always give
// every group equal width, but this needs a wide "this year" stack next to
// a visibly narrower, paler "last year" stack — not something the library
// supports, so this draws it directly instead of fighting the library for it.
const MonthlyStackedBarChart = ({ bars, height = 320 }) => {
  const [tooltip, setTooltip] = useState(null); // { x, y, lines }
  const containerRef = useRef(null);
  // Bars grow up from the baseline instead of just appearing — starts false
  // on every mount/data change, then flips true a frame later so the CSS
  // transition on each rect's height/y actually has a "from" state to
  // animate away from.
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    setAnimate(false);
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimate(true));
    });
    return () => cancelAnimationFrame(raf1);
  }, [bars]);
  // Tracks the container's real pixel width so the SVG's viewBox can match
  // it 1:1 — a fixed viewBox stretched via preserveAspectRatio would scale x
  // and y by different factors, distorting text, corner radii and strokes.
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const W = width;
  const H = height;
  const plotLeft = 42;
  const plotRight = 12;
  const plotTop = 26;
  const plotBottom = H - 34;
  const plotHeight = plotBottom - plotTop;
  const plotWidth = W - plotLeft - plotRight;

  const rawMax = Math.max(1, ...bars.flatMap((bar) => [bar.currentTotal, bar.previousTotal]));
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const norm = rawMax / magnitude;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const niceMax = niceNorm * magnitude || 1;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(niceMax * f));

  const yFor = (value) => plotBottom - (value / niceMax) * plotHeight;
  const slotWidth = bars.length ? plotWidth / bars.length : plotWidth;
  // This-year bar is ~2.6x wider than last-year's — a wide bold stack next
  // to a visibly narrower, paler one.
  const mainWidth = slotWidth * 0.42;
  const gap = slotWidth * 0.06;
  const prevWidth = slotWidth * 0.16;
  const clusterPad = (slotWidth - (mainWidth + gap + prevWidth)) / 2;

  const showTooltip = (evt, lines) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({ x: evt.clientX - rect.left, y: evt.clientY - rect.top, lines });
  };

  const renderStack = (segments, x, width, total, barIndex) => {
    let cursor = plotBottom;
    // Stagger each bar's growth slightly so the chart reads as a left-to-
    // right wave rather than every bar popping in at once — subtle, capped
    // so it stays snappy even with 12 months.
    const delay = Math.min(barIndex * 18, 200);
    return segments.map((seg) => {
      const segHeight = (seg.value / niceMax) * plotHeight;
      const y = cursor - segHeight;
      cursor = y;
      if (seg.value <= 0) return null;
      return (
        <rect
          key={seg.bucket}
          x={x}
          y={animate ? y : plotBottom}
          width={width}
          height={animate ? Math.max(segHeight, 0) : 0}
          fill={seg.color}
          rx={2}
          style={{ transition: `height 650ms cubic-bezier(.22,.61,.36,1) ${delay}ms, y 650ms cubic-bezier(.22,.61,.36,1) ${delay}ms` }}
          onMouseMove={(e) => showTooltip(e, [`${seg.bucket}: ${formatNumber(seg.value)}`, `Total: ${formatNumber(total)}`])}
          onMouseLeave={() => setTooltip(null)}
        />
      );
    });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={plotLeft} x2={W - plotRight} y1={yFor(t)} y2={yFor(t)} stroke="#eef0f4" strokeDasharray="4 4" />
            <text x={plotLeft - 8} y={yFor(t) + 4} textAnchor="end" fontSize="11" fill="#8a8f98">
              {formatNumber(t)}
            </text>
          </g>
        ))}
        {bars.map((bar, i) => {
          const slotX = plotLeft + i * slotWidth;
          const mainX = slotX + clusterPad;
          const prevX = mainX + mainWidth + gap;
          return (
            <g key={`${bar.label}-${i}`}>
              {renderStack(bar.current, mainX, mainWidth, bar.currentTotal, i)}
              {renderStack(bar.previous, prevX, prevWidth, bar.previousTotal, i)}
              {bar.currentTotal > 0 && (
                <AnimatedSvgText
                  value={bar.currentTotal}
                  x={mainX + mainWidth / 2}
                  y={yFor(bar.currentTotal) - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="#495057"
                  style={{ transition: `opacity 400ms ease ${Math.min(i * 18, 200) + 300}ms`, opacity: animate ? 1 : 0 }}
                />
              )}
              <text x={slotX + slotWidth / 2} y={plotBottom + 22} textAnchor="middle" fontSize="11" fill="#495057">
                {bar.label}
              </text>
            </g>
          );
        })}
      </svg>
      {tooltip && (
        <div className="oa-chart-tooltip" style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}>
          {tooltip.lines.map((line) => <div key={line}>{line}</div>)}
        </div>
      )}
    </div>
  );
};

const MonthlyBreakdownWidgets = () => {
  const [monthlyBreakdown, setMonthlyBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Always this calendar year vs last — recomputed on every load, so
        // it's correct on its own every January without a code change.
        const thisYear = new Date().getFullYear();
        const params = { mode: 'calendar', a: String(thisYear - 1), b: String(thisYear), dimension: 'collection' };
        const { data } = await axios.get(`${API_BASE_URL}/api/dashboard/order-analytics/monthly-breakdown`, {
          params,
          ...authHeader(),
        });
        if (!cancelled) setMonthlyBreakdown(data);
      } catch (err) {
        console.error('Error fetching monthly breakdown:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const monthlyChartData = useMemo(() => {
    if (!monthlyBreakdown) return null;
    const { buckets, a, b } = monthlyBreakdown;
    const maxLen = Math.max(a.months.length, b.months.length);
    const bucketColors = buckets.map((_, i) => MONTHLY_PALETTE[i % MONTHLY_PALETTE.length]);
    const lightBucketColors = bucketColors.map((c) => lightenHex(c));
    const bars = Array.from({ length: maxLen }, (_, i) => {
      const bMonth = b.months[i];
      const aMonth = a.months[i];
      const current = buckets.map((bucket, bi) => ({
        bucket,
        value: bMonth?.values?.[bucket]?.order_count ?? 0,
        color: bucketColors[bi],
      }));
      const previous = buckets.map((bucket, bi) => ({
        bucket,
        value: aMonth?.values?.[bucket]?.order_count ?? 0,
        color: lightBucketColors[bi],
      }));
      return {
        label: bMonth?.month_label ?? aMonth?.month_label ?? '',
        current,
        previous,
        currentTotal: current.reduce((sum, seg) => sum + seg.value, 0),
        previousTotal: previous.reduce((sum, seg) => sum + seg.value, 0),
      };
    });
    // `b` is always the current calendar year (fetched dynamically above), so
    // its months run Jan..Dec in order — slicing to the real current month
    // (recomputed on every render, never hardcoded) drops the still-to-come
    // months instead of showing empty bars for a year that hasn't happened yet.
    const currentMonthNum = new Date().getMonth() + 1;
    return { bars: bars.slice(0, currentMonthNum), buckets, bucketColors };
  }, [monthlyBreakdown]);

  const monthlyRevenueRows = useMemo(() => {
    if (!monthlyBreakdown) return [];
    const { a, b } = monthlyBreakdown;
    // Same rule as the chart: `b` is always the current calendar year, so
    // stop at the real current month (recomputed fresh, never hardcoded)
    // instead of listing months that haven't happened yet.
    const currentMonthNum = new Date().getMonth() + 1;
    const maxLen = Math.min(Math.max(a.months.length, b.months.length), currentMonthNum);
    return Array.from({ length: maxLen }, (_, i) => ({
      month_label: b.months[i]?.month_label ?? a.months[i]?.month_label ?? '',
      lastYearRevenue: a.months[i]?.total?.revenue_eur ?? 0,
      thisYearRevenue: b.months[i]?.total?.revenue_eur ?? 0,
      // A month with €0 in both years is just a month nothing happened in —
      // not worth a row in a table meant to highlight actual revenue.
    })).filter((row) => row.lastYearRevenue !== 0 || row.thisYearRevenue !== 0);
  }, [monthlyBreakdown]);

  return (
    <Fragment>
      <style>{`
        /* !important beats the theme's ".card .card-header > span { display: block; }"
           rule in _card.scss, which otherwise wins on specificity and collapses this
           badge into a block box, pushing the icon to the top-left instead of centering it. */
        .oa-card-header-icon { display: inline-flex !important; align-items: center !important; justify-content: center !important; width: 32px; height: 32px; border-radius: 8px; background: ${PRIMARY}1a; color: ${PRIMARY}; margin-right: 10px; margin-top: 0 !important; }
        .oa-card-header-icon svg { display: block; }
        .oa-table tbody tr { transition: background-color .12s ease; }
        @keyframes rfFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .oa-animate-in { animation: rfFadeUp 480ms cubic-bezier(.22,.61,.36,1) both; }
        .oa-chart-tooltip {
          position: absolute;
          background: rgba(33, 37, 41, 0.92);
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.4;
          pointer-events: none;
          white-space: nowrap;
          transform: translateY(-100%);
          z-index: 5;
        }
        .oa-revenue-panel {
          border-left: 1px solid #eef0f4;
          padding-left: 20px;
          height: 100%;
        }
        @media (max-width: 991.98px) {
          .oa-revenue-panel { border-left: none; border-top: 1px solid #eef0f4; padding-left: 0; padding-top: 16px; margin-top: 16px; }
        }
        .oa-revenue-scroll {
          max-height: 186px;
          overflow-y: auto;
        }
        .oa-revenue-scroll table { font-size: 12.5px; }
        .oa-revenue-scroll thead th {
          position: sticky;
          top: 0;
          background: #fff;
          z-index: 1;
        }
      `}</style>
      <Row>
        <Col md="12" className="mb-3">
          <Card className="oa-animate-in">
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <span className="oa-card-header-icon"><BarChart2 size={16} /></span>
                <h5 className="mb-0">Monthly Breakdown</h5>
              </div>
              <small className="text-muted">Orders by Collection</small>
            </CardHeader>
            <CardBody>
              <Row>
                <Col lg="8" md="12">
                  {loading && (
                    <div className="text-center py-5">
                      <Spinner />
                    </div>
                  )}
                  {!loading && monthlyChartData && monthlyChartData.bars.length > 0 && (
                    <Fragment>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <Badge color="light" className="text-dark d-inline-flex align-items-center gap-1">
                          <span className="d-inline-block rounded-circle" style={{ width: 8, height: 8, background: PRIMARY }} />
                          This year ({monthlyBreakdown.b.label})
                        </Badge>
                        <small className="text-muted">vs</small>
                        <Badge color="light" className="text-dark d-inline-flex align-items-center gap-1">
                          <span className="d-inline-block rounded-circle" style={{ width: 8, height: 8, background: MUTED }} />
                          Last year ({monthlyBreakdown.a.label})
                        </Badge>
                      </div>
                      {/* Custom legend: the chart draws two stacks per bucket (bold =
                          this year, pale = last year), so a series-based legend would
                          list every bucket twice. This shows each bucket once plus a
                          single "same month last year" swatch. */}
                      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        {monthlyBreakdown.buckets.map((bucket, i) => (
                          <span key={bucket} className="d-inline-flex align-items-center gap-1">
                            <span
                              className="d-inline-block rounded-circle"
                              style={{ width: 10, height: 10, background: monthlyChartData.bucketColors[i] }}
                            />
                            <small>{bucket}</small>
                          </span>
                        ))}
                        <span className="d-inline-flex align-items-center gap-1">
                          <span className="d-inline-block rounded-circle" style={{ width: 10, height: 10, background: MUTED }} />
                          <small className="text-muted">Same month last year ({monthlyBreakdown.a.label})</small>
                        </span>
                      </div>
                      <MonthlyStackedBarChart bars={monthlyChartData.bars} height={320} />
                    </Fragment>
                  )}
                </Col>

                <Col lg="4" md="12">
                  <div className="oa-revenue-panel">
                    <div className="d-flex align-items-center mb-2">
                      <span className="oa-card-header-icon"><DollarSign size={16} /></span>
                      <h6 className="mb-0">Monthly Revenue</h6>
                    </div>
                    {loading && (
                      <div className="text-center py-4">
                        <Spinner size="sm" />
                      </div>
                    )}
                    {!loading && monthlyBreakdown && (
                      <div className="oa-revenue-scroll table-responsive">
                        <Table hover size="sm" className="oa-table mb-0">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th className="text-end" style={{ color: MUTED }}>{monthlyBreakdown.a.label}</th>
                              <th className="text-end" style={{ color: PRIMARY }}>{monthlyBreakdown.b.label}</th>
                              <th className="text-end">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthlyRevenueRows.map((row) => (
                              <tr key={row.month_label}>
                                <td>{row.month_label}</td>
                                <td className="text-end"><AnimatedNumber value={row.lastYearRevenue} format={formatCurrency} /></td>
                                <td className="text-end"><AnimatedNumber value={row.thisYearRevenue} format={formatCurrency} /></td>
                                <td className="text-end">
                                  <DeltaBadge value={percentDelta(row.lastYearRevenue, row.thisYearRevenue)} />
                                </td>
                              </tr>
                            ))}
                            {monthlyRevenueRows.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-3">No revenue in either year</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default MonthlyBreakdownWidgets;
